

<template>
  <v-card align="center" justify="center" class="pa-4">
    <div class="d-flex w-100">
      <div class="trades-container w-50 pr-4 d-flex flex-column">
        <div class="trade-line-header">
          <div class="buy-header-container">
            <span class="header-text">BUY</span>
            <div class="header-line green-line"></div>
          </div>
          <div class="sell-header-container">
            <span class="header-text">SELL</span>
            <div class="header-line red-line"></div>
          </div>
        </div>
  
        <div class="trade-sections-wrapper flex-grow-1">
          <div class="trade-section" v-if="!loadingNegative">
            <div v-for="(pair, i) in negativeTrades" :key="'neg' + i" class="trade-item">
              <div class="trade-line">
                <img src="/nr.jpeg" alt="Negative Trade" class="trade-icon" />
                <span>
                  <span class="buy">{{ pair.buyLeg.symbol }} | {{ pair.buyLeg.ltp }} | {{ pair.buyLeg.quantity }} | {{
                    pair.buyLeg.expiryDate }}</span>
                  →
                  <span class="sell">{{ pair.sellLeg.symbol }} | {{ pair.sellLeg.ltp }} | {{ pair.sellLeg.quantity }} |
                    {{ pair.sellLeg.expiryDate }}</span>
                </span>
                <v-btn icon variant="text" size="x-small" @click="loadNegative" class="refresh-btn">
                  <v-icon size="16">mdi-refresh</v-icon>
                </v-btn>
                <v-checkbox v-model="selectedNegativeTrade" :value="pair" class="small-checkbox" hide-details
                  @change="updateTextarea"></v-checkbox>
              </div>
            </div>
          </div>
  
          <div class="trade-section" v-if="!loadingPositive">
            <div v-for="(pair, i) in positiveTrades" :key="'pos' + i" class="trade-item">
              <div class="trade-line">
                <img src="/pr.jpeg" alt="Positive Trade" class="trade-icon" />
                <span>
                  <span class="buy">{{ pair.buyLeg.symbol }} | {{ pair.buyLeg.ltp }} | {{ pair.buyLeg.quantity }} | {{
                    pair.buyLeg.expiryDate }}</span>
                  →
                  <span class="sell">{{ pair.sellLeg.symbol }} | {{ pair.sellLeg.ltp }} | {{ pair.sellLeg.quantity }} |
                    {{ pair.sellLeg.expiryDate }}</span>
                </span>
                <v-btn icon variant="text" size="x-small" @click="loadPositive" class="refresh-btn">
                  <v-icon size="14">mdi-refresh</v-icon>
                </v-btn>
                <v-checkbox v-model="selectedPositiveTrades" :value="pair" class="small-checkbox" hide-details
                  @change="updateTextarea"></v-checkbox>
              </div>
            </div>
          </div>
  
          <div class="trade-section" v-if="!loadingDelta">
            <div v-for="(pair, i) in deltaTrades" :key="'delta' + i" class="trade-item">
              <div class="trade-line">
                <img src="/dm.png" alt="Delta Trade" class="trade-icon" />
                <span>
                  <span class="buy">{{ pair.buyLeg.symbol }} | {{ pair.buyLeg.ltp }} | {{ pair.buyLeg.quantity }} | {{
                    pair.buyLeg.expiryDate }}</span>
                  →
                  <span class="sell">{{ pair.sellLeg.symbol }} | {{ pair.sellLeg.ltp }} | {{ pair.sellLeg.quantity }} |
                    {{ pair.sellLeg.expiryDate }}</span>
                </span>
                <v-btn icon variant="text" size="x-small" @click="loadDelta" class="refresh-btn">
                  <v-icon size="14">mdi-refresh</v-icon>
                </v-btn>
                <v-checkbox v-model="selectedDeltaTrades" :value="pair" class="small-checkbox" hide-details
                  @change="updateTextarea"></v-checkbox>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <div class="w-50 d-flex flex-column align-center justify-center pl-4">
        <v-card align="center" justify="center" class="pa-4 pt-4 elevation-0"></v-card>
        <v-textarea v-model="textareaData" label="Selected Trades" rows="6" class="custom-textarea smaller-textarea"
          bg-color="#f5f5f5"></v-textarea>
        <div class="d-flex justify-center mt-4">
          <v-btn color="primary" @click="placeOrder" :disabled="isButtonDisabled">Place Order</v-btn>
        </div>
      </div>
    </div>
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" location="bottom right">
      {{ snackbarMessage }}
    </v-snackbar>
  
  </v-card>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import store from "@/store";

export default {
  setup() {
    const horizon = ref(4);
    const negativeTrades = ref([]);
    const positiveTrades = ref([]);
    const deltaTrades = ref([]);
    const loadingNegative = ref(false);
    const loadingPositive = ref(false);
    const loadingDelta = ref(false);
    const selectedNegativeTrade = ref(null);
    const selectedPositiveTrades = ref([]);
    const selectedDeltaTrades = ref([]);
    const textareaData = ref('');
    const loading = ref(false);
    const snackbar = ref(false);
    const snackbarMessage = ref("");
    const snackbarColor = ref("success");
    const orderDisplays = ref([]);
    const loadNegative = async () => {
      loadingNegative.value = true;
      try {
        const res = await store.dispatch("strategy/getNegativeTrade");
        negativeTrades.value = res?.tradePosiionList || [];
      } finally {
        loadingNegative.value = false;
      }
    };
    const loadPositive = async () => {
      loadingPositive.value = true;
      try {
        const res = await store.dispatch("strategy/getPositiveTrade");
        positiveTrades.value = res?.tradePosiionList || [];
      } finally {
        loadingPositive.value = false;
      }
    };
    const loadDelta = async () => {
      loadingDelta.value = true;
      try {
        const res = await store.dispatch("strategy/getDeltaManagementTrade", { horizon: horizon.value });
        deltaTrades.value = res?.tradePosiionList || [];
      } finally {
        loadingDelta.value = false;
      }
    };
    const isButtonDisabled = computed(() => {
      // The button will be disabled if textareaData is empty or just whitespace
      return !textareaData.value.trim();
    });
    const updateTextarea = () => {
      const selectedTrades = [];

      if (selectedNegativeTrade.value) {
        const buyLeg = selectedNegativeTrade.value.buyLeg;
        const sellLeg = selectedNegativeTrade.value.sellLeg;
        selectedTrades.push(
          `NR: Buy | ${buyLeg.symbol} | ${buyLeg.ltp} | ${buyLeg.quantity} | ${buyLeg.expiryDate}\n` +
          `    Sell| ${sellLeg.symbol} | ${sellLeg.ltp} | ${sellLeg.quantity} | ${sellLeg.expiryDate}`
        );
      }

      selectedPositiveTrades.value.forEach(trade => {
        const buyLeg = trade.buyLeg;
        const sellLeg = trade.sellLeg;
        selectedTrades.push(
          `PR:  Buy | ${buyLeg.symbol} | ${buyLeg.ltp} | ${buyLeg.quantity} | ${buyLeg.expiryDate}\n` +
          `     Sell| ${sellLeg.symbol} | ${sellLeg.ltp} | ${sellLeg.quantity} | ${sellLeg.expiryDate}`
        );
      });

      selectedDeltaTrades.value.forEach(trade => {
        const buyLeg = trade.buyLeg;
        const sellLeg = trade.sellLeg;
        selectedTrades.push(
          `DM:  Buy | ${buyLeg.symbol} | ${buyLeg.ltp} | ${buyLeg.quantity} | ${buyLeg.expiryDate}\n` +
          `     Sell| ${sellLeg.symbol} | ${sellLeg.ltp} | ${sellLeg.quantity} | ${sellLeg.expiryDate}`
        );
      });

      textareaData.value = selectedTrades.join('\n\n');
    };


    const parseOrderLine = (line) => {
      if (!line) return null;

      // remove leading labels like "NR:", "PR:", "DM:"
      const clean = line.replace(/^\s*(NR|PR|DM)\s*:\s*/i, "").trim();

      // tolerant split
      const parts = clean.split("|").map(p => p.trim());
      if (parts.length < 5) return null;

      let side = parts[0]; // Buy / Sell
      const symbol = parts[1];
      const price = parts[2];
      const qty = parts[3];
      const date = parts[4];

      const type = /CE$/i.test(symbol) ? "Call" : /PE$/i.test(symbol) ? "Put" : "";

      // --- FIX: capture the last 4–6 digits before CE/PE (NIFTY25OCT24500CE, NIFTY2590224500CE etc.)
      const strikeMatch = symbol.match(/(\d{4,6})(?=(?:CE|PE)$)/i);
      const strike = strikeMatch ? strikeMatch[1] : "";

      if (!type || !/^(Buy|Sell)$/i.test(side) || !strike) return null;

      side = side.charAt(0).toUpperCase() + side.slice(1).toLowerCase();

      return `${type} ${side} ${strike} ${date} ${qty} ${price} ${symbol}`;
    };

    const placeOrder = async () => {
      console.log("placeOrder", textareaData.value);
      if (!textareaData.value || textareaData.value.length === 0) {
        showSnackbar("No trade order details to place.", "error");
        return;
      }
      loading.value = true;
      try {
        const lines = textareaData.value.split("\n").map(l => l.trim()).filter(Boolean);
        const tradeOrderStrings = lines
          .filter(l => l.includes("Buy") || l.includes("Sell"))
          .map(parseOrderLine)
          .filter(Boolean);

        console.log("tradeOrderStrings", tradeOrderStrings);
        const resp = await store.dispatch("orders/placeOrderList", { tradeOrderDetails: tradeOrderStrings });
        console.log("✅ placeOrder resp:", resp);

        orderDisplays.value = resp && Array.isArray(resp.orderDisplayList)
          ? resp.orderDisplayList
          : [];

        // 🔥 Convert orderDisplays to string and put into textarea
        if (orderDisplays.value.length > 0) {
          const formatted = orderDisplays.value.map(o =>
            `${o.exchange_order_id} | ${o.stock_code} | ${o.action} | ${o.quantity} | ${o.average_price} | ${o.status}`
          ).join("\n");

          textareaData.value = formatted;
        }

        showSnackbar("Order placed successfully!", "success");
      } catch (error) {
        console.error("❌ placeOrder error:", error);
        showSnackbar("Failed to place order.", "error");
      } finally {
        loading.value = false;
      }
    };



    const showSnackbar = (message, color) => {
      snackbar.value = false; // reset first
      snackbarMessage.value = message;
      snackbarColor.value = color;
      setTimeout(() => {
        snackbar.value = true;
      }, 50);
    };

    // Watch for changes in all selection models and update the textarea
    watch(selectedNegativeTrade, updateTextarea);
    watch(selectedPositiveTrades, updateTextarea);
    watch(selectedDeltaTrades, updateTextarea);

    onMounted(() => {
      loadNegative();
      loadPositive();
      loadDelta();
    });

    return {
      horizon,
      negativeTrades,
      positiveTrades,
      deltaTrades,
      loadingNegative,
      loadingPositive,
      loadingDelta,
      selectedNegativeTrade,
      selectedPositiveTrades,
      selectedDeltaTrades,
      textareaData,
      loadNegative,
      loadPositive,
      loadDelta,
      updateTextarea,
      isButtonDisabled,
      orderDisplays,
      placeOrder,
      showSnackbar,
      snackbar,
      snackbarMessage,
      snackbarColor,
    };
  }
};
</script>
<style scoped>
/* (style block remains the same, except for the .w-25 class, which is no longer needed) */

.w-75 {
  width: 75%;
}

.pr-4 {
  padding-right: 16px;
}

.trades-container {
  font-family: monospace;
  display: flex;
  flex-direction: column;
  padding: 16px;
}

.trade-sections-wrapper {
  overflow-y: auto;
}

.trade-section {
  margin-bottom: 8px;
}

.trade-line {
  display: flex;
  align-items: center;
  /* Removed white-space: nowrap; */
  width: 100%;
  overflow: hidden;
  gap: 4px;
}

.trade-line>span {
  flex-grow: 1;
  /* Allows the span to take up the remaining space */
  flex-shrink: 1;
  /* Allows the span to shrink if content is long */
  overflow: hidden;
  /* Prevents text overflow from affecting the layout */
  text-overflow: ellipsis;
  /* Adds "..." for long text */
  white-space: nowrap;
  /* Keep the text on one line within its limited space */
}

.trade-line-header {
  display: flex;
  /* Add this line to enable flexbox layout */
  align-items: center;
  gap: 4px;
  padding-left: 16px;
  padding-right: 16px;
}

/* 1. Reduce checkbox and label size, make it rounded */
.small-checkbox {
  padding: 0;
  margin: 0;
}

.small-checkbox :deep(.v-selection-control__input) {
  width: 20px;
  height: 20px;
}

.small-checkbox :deep(.v-label) {
  font-size: 0.8em;
  padding-left: 4px;
}

.small-checkbox :deep(.v-icon) {
  font-size: 16px;
}

.small-checkbox :deep(.v-selection-control__wrapper) {
  border-radius: 5px;
}

.buy {
  color: #4caf50;
  font-weight: bold;
  font-size: 0.7em;
  /* Reduced from 0.9em */
}

.sell {
  color: #f44336;
  font-weight: bold;
  font-size: 0.7em;
  /* Reduced from 0.9em */
}

/* Reduced space between tradeline data and mdi-refresh */
.refresh-btn {
  margin-left: 0;
}

/* 4. Dropdown styling */
.delta-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  /* Reduced gap to make it compact */
  margin-left: auto;
  /* Push delta controls to the right if needed */
}

.horizon-select {
  width: 70px;
  font-size: 0.8em;
}

/* Adjust the internal components of the v-select */
:deep(.horizon-select .v-input__control) {
  min-height: 24px !important;
  border-radius: 4px;
}

:deep(.horizon-select .v-field) {
  height: 24px !important;
  padding: 0 4px !important;
  background-color: #f5f5f5 !important;
  border: 1px solid #ccc !important;
}

:deep(.horizon-select .v-field__input) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 0.9rem;
  color: #333;
}

:deep(.horizon-select .v-field__append-inner) {
  padding-top: 0 !important;
  align-self: center;
}

.custom-textarea {
  /* Keep your main styles here */
  height: 70%;
  border-radius: 8px;
  padding: 8px;
  font-family: monospace;

  border: 1px solid #ced0d1;
  /* This adds a 1px solid blue border */
}

/* NEW CLASS to make the textarea 20% smaller */
.smaller-textarea {
  width: 80%;
  /* 100% - 20% = 80% */
}

:deep(.custom-textarea .v-field__input) {
  font-size: 0.7rem !important;
  /* Use !important to override Vuetify's default styles */
}

/* Aggressively remove all internal Vuetify styling */
:deep(.v-textarea .v-input__control) {
  border: none !important;
  box-shadow: none !important;
}

:deep(.v-textarea .v-field) {
  background-color: transparent !important;
  /* Make the field itself transparent */
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  /* Remove any padding that might add space */
}

:deep(.v-textarea .v-field__field) {
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}

:deep(.v-textarea .v-field__outline) {
  display: none !important;
  /* Hide the outline element completely */
}

/* Adjust the label to float correctly */
:deep(.v-label) {
  color: #666;
  /* Adjust color for visibility */
  top: 8px !important;
  /* Position the label at the top of your custom box */
  left: 8px !important;
}

:deep(.v-field__field) {
  margin-top: 15px !important;
  /* Add space for the label */
}

.trade-item {
  margin-bottom: 2px;
}

.trade-icon {
  width: 30px;
  /* Adjust as needed */
  height: 30px;
  /* Adjust as needed */
  margin-right: 4px;
  /* Spacing between the icon and the trade details */
}

/* Update the trade header to be aligned with the trade container below */
.trade-line {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  width: 100%;
  overflow: hidden;
  gap: 4px;
}

.buy-header-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  /* Allows the buy header to expand */
  align-items: center;
}

.sell-header-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  /* Allows the sell header to expand */
  align-items: center;
}

.header-text {
  font-weight: bold;
  font-size: 1rem;
}

.header-line {
  height: 2px;
  width: 100%;
  margin-top: 4px;
}

.green-line {
  background-color: #4caf50;
}

.red-line {
  background-color: #f44336;
}

.w-100 {
  width: 100%;
}
</style>